import { unescape } from "lodash";

export type Column = string;
export type Row = number; 
export type CellRange = {
    startCol: Column,
    startRow: Row,
    endCol: Column,
    endRow: Row
}

export enum Alignment{ Left='Left', Center='Center', Right='Right' }
export enum Edge { Top=1, Right=2, Bottom=4, Left=8, All=15}
export enum StandardColors {  Black='black', Red='red', Green='green', Blue='blue', Brown='Brown', Maroon='maroon', Gray='gray', Yellow='yellow', Lime='lime', Aqua='aqua', Pink='pink', Orange='orange', Silver='silver' }
export enum CellFormats {
    //eslint-disable-next-line
    Number_Accounting='_("$"* #,##0.00_);_("$"* \(#,##0.00\);_("$"* "-"??_);_(@_)',
    Number_Percent_2='p',
    Text_Unformatted=''
}

export interface ISpreadsheetGrid {
    startUpdate(): void;
    endUpdate(): void;
    ensureColumnIndexes(newSheetColumnIndexSize: number): void;
    ensureRowIndexes(newSheetRowIndexSize: number): void;
    getLastColumn(): string;
    getLastColumnIndex(): number;
    getlastRowIndex(): number;
    getCellValue(row: Row, col: Column): any;
    setCellValue(row: Row, col: Column, value: string|number, format: CellFormats): void;
    getCellFormula(row: Row, col: Column): string;
    setCellFormula(row: Row, col: Column, value: string, format: CellFormats): void;
    setCellWordWrap(row: Row, col: Column, wordWrap: boolean): void;
    setCellColor(row: Row, col: Column, color: StandardColors): void;
    setCellAlign(row: Row, col: Column, align: Alignment): void;
    setCellBorder(row: Row, col: Column, edge: Edge, width: number): void;
    setCellStrike(row: Row, col: Column, bold: boolean): void;
    setCellUnderline(row: Row, col: Column, bold: boolean): void;
    setCellItalic(row: Row, col: Column, bold: boolean): void;
    setCellBold(row: Row, col: Column, bold: boolean): void;
    setSpanRange(row: Row, col: Column, row2: Row, col2: Column): void;
    setColWidth(col: Column, newWidth: number): void;
    setRowHeight(row: Row, height: number): void;
    AutoUpdateRowHeight(row: Row): void
    moveCells(source: CellRange, destinationStartRow: Row, destinationStartCol: Column): void;
    copyCells(source: CellRange, destinationStartRow: Row, destinationStartCol: Column): void;
    getColumnCount(startCol: Column, endCol: Column) : number;
    getNextCol(col: Column, count?: number):string;
    getPrevCol(col: Column, count?: number):string;
    getLetterColFromIndex(columnIndex: number): string;
    getIndexColFromLetter(letter: string): number;
    isColBetween(col: Column, a: Column, b: Column): boolean;
    compareCols(a: Column, b: Column) : 0 | 1 | -1;
    saveSpreadsheet(): void;
}

export class TreeGridSS implements ISpreadsheetGrid {
    
    constructor(private treegrid : TGrid) { }

    startUpdate() {
        this.treegrid?.StartUpdate();
    }

    endUpdate() {
        this.treegrid?.EndUpdate();
    }

    ensureColumnIndexes(newSheetColumnIndexSize: number): void {
        const lastColIndex = this.getLastColumnIndex();
        const increaseSize = newSheetColumnIndexSize - lastColIndex;

        if(increaseSize <= 0) return;

        this.treegrid?.AddColPage(increaseSize);
    }

    ensureRowIndexes(newSheetRowIndexSize: number): void {
        const lastRowIndex = this.getlastRowIndex();
        const increaseSize = newSheetRowIndexSize - lastRowIndex;

        if(increaseSize <= 0) return;
        
        for(let r=0; r<=increaseSize; r++) {
            this.treegrid?.AddRow(undefined, undefined, 1);
        }
    }

    getLastColumn(): string {
        return this.treegrid?.GetLastCol() as string;
    }

    getLastColumnIndex(): number {
        const lastCol = this.getLastColumn();

        return this.treegrid?.GetColIndex(lastCol);
    }

    getlastRowIndex(): number {
        const lastRow = this.treegrid?.GetLastDataRow() as TRow;
        const lastRowIndex = lastRow ? this.treegrid?.GetRowIndex(lastRow) as number : 1;

        return lastRowIndex;
    }

    getCellValue(row: Row, col: Column) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;
        let value = this.treegrid?.GetValue(tRow, col);

        if (typeof value === 'string') {
            value = unescape(value);
        }

        return value;
    }

    setCellValue(row: Row, col: Column, value: any, format: CellFormats) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        // if cell is a formula then it must be cleared first otherwise cell won't be replaced properly
        if(this.getCellFormula(row, col)) { 
            this.treegrid?.SetAttribute(tRow, col, 'EFormula', null, 0);
        }

        this.treegrid?.SetValue(tRow, col, value, true);
        this.treegrid?.SetAttribute(tRow, col, 'Format', format, true);
    }

    getCellFormula(row: Row, col: Column) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        return this.treegrid?.GetAttribute(tRow, col, 'EFormula') as string;
    }

    setCellFormula(row: Row, col: Column, formula: string, format: CellFormats) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.SetAttribute(tRow, col, 'EFormula', formula, 1);
        this.treegrid?.Calculate(1);
        this.treegrid?.SetAttribute(tRow, col, 'Format', format, true);
    }

    setCellWordWrap(row: Row, col: Column, wordWrap: boolean) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.SetAttribute(tRow, col, 'Wrap', wordWrap, true );
    }

    setCellColor(row: Row, col: Column, color: StandardColors) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.SetCellStyle(tRow, col, { Color:'245, 245, 245' }, true);
    }

    setCellAlign(row: Row, col: Column, align: Alignment) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.SetAttribute(tRow, col, 'Align', align, true);
    }

    setCellBorder(row: Row, col: Column, edge: Edge, width: number) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.SetBorder(tRow, col, [width,'Black',0], edge);
    }


    setCellStrike(row: Row, col: Column, strike: boolean) {
        this.setTextStyleBits(row, col, 1, strike);
    }

    setCellUnderline(row: Row, col: Column, underline: boolean) {
        this.setTextStyleBits(row, col, 2, underline);
    }

    setCellItalic(row: Row, col: Column, italic: boolean) {
        this.setTextStyleBits(row, col, 3, italic);
    }

    setCellBold(row: Row, col: Column, bold: boolean) {
        this.setTextStyleBits(row, col, 4, bold);
    }

    setSpanRange(row: Row, col: Column, row2: Row, col2: Column) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;
        const tRow2 = this.treegrid?.GetRowByIndex(row2) as TRow;

        this.treegrid?.SpanRange(tRow, col, tRow2, col2);
    }

    private setTextStyleBits(row: Row, col: Column, position: number, value: boolean) {
        const paddingFormat = '0000';
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        const cellStyleNumStr = this.treegrid?.GetAttribute(tRow, col, 'TextStyle') as string;
        let cellStyleBitStr = Number(cellStyleNumStr).toString(2);

        cellStyleBitStr = paddingFormat + cellStyleBitStr;
        cellStyleBitStr = cellStyleBitStr.substr(cellStyleBitStr.length-paddingFormat.length);

        const cellStyleBitAry = cellStyleBitStr.split('');

        cellStyleBitAry[position-1] = value ? '1' : '0';
        const newCellStyleBitStr = cellStyleBitAry.join('');

        const newCellStyleNum = parseInt(newCellStyleBitStr, 2);

        this.treegrid?.SetAttribute(tRow, col, 'TextStyle', newCellStyleNum.toString(), true);
    }

    setColWidth(col: Column, newWidth: number) {
        // @ts-ignore: type error
        const currCol = this.treegrid?.Cols[col] as TCol;
        const currWidth = currCol.Width;
        const diff =  newWidth - currWidth;

        this.treegrid?.SetWidth(col, diff);
    }

    setRowHeight(row: Row, height: number) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.ResizeRow(tRow, height);
    }

    AutoUpdateRowHeight(row: Row) {
        const tRow = this.treegrid?.GetRowByIndex(row) as TRow;

        this.treegrid?.UpdateRowHeight(tRow, true);
    }

    moveCells(source: CellRange, destinationStartRow: Row, destinationStartCol: Column) {
        this.copyMoveCells(source, destinationStartRow, destinationStartCol, 1);
    }

    copyCells(source: CellRange, destinationStartRow: Row, destinationStartCol: Column) {
        this.copyMoveCells(source, destinationStartRow, destinationStartCol, 0);
    }

    private copyMoveCells(source: CellRange, destStartRow: Row, destStartCol: Column, deleteSource: number) {
        const srcStartTRow = this.treegrid?.GetRowByIndex(source.startRow) as TRow;
        const srcStartCol = source.startCol;
        const srcEndTRow = this.treegrid?.GetRowByIndex(source.endRow) as TRow;
        const srcEndCol = source.endCol;

        const destStartTRow = this.treegrid?.GetRowByIndex(destStartRow) as TRow;

        const destEndRowIndex = destStartRow + (source.endRow - source.startRow);
        const destEndTRow = this.treegrid?.GetRowByIndex(destEndRowIndex) as TRow;

        const count = this.getColumnCount(source.startCol, srcEndCol);
        const destEndCol = this.getNextCol(destStartCol, count);


        const sourceArray =  [srcStartTRow, srcStartCol, srcEndTRow, srcEndCol];
        const destinationArray = [destStartTRow, destStartCol, destEndTRow, destEndCol];

        this.treegrid?.CopyRange(destinationArray, sourceArray, deleteSource);
    }

    getColumnCount(startCol: Column, endCol: Column) {
        const startColIndex = this.getIndexColFromLetter(startCol) -1; 
        const endColIndex = this.getIndexColFromLetter(endCol);

        return endColIndex - startColIndex;
    }

    getLetterColFromIndex(columnIndex: number) {
        let temp, letter = '';

        while (columnIndex > 0) {
            temp = (columnIndex - 1) % 26;
            letter = String.fromCharCode(temp + 65) + letter;
            columnIndex = (columnIndex - temp - 1) / 26;
        }
        return letter;
    }

    getIndexColFromLetter(letter: string) {
        const length = letter.length;
        let column = 0;
        
        for (let i = 0; i < length; i++) {
            column += (letter.charCodeAt(i) - 64) * Math.pow(26, length - i - 1);
        }
        return column;
    }

    getPrevCol(col: string, count: number = 1):string {
        return this.getNextCol(col, -count); 
    }

    getNextCol(col: string, count: number = 1):string {
        if(count === 0) return col;

        const index = this.getIndexColFromLetter(col);
        const nextLetter = this.getLetterColFromIndex(index+count);

        return nextLetter; 
    }

    isColBetween(col: Column, a: Column, b: Column) {
        const comp1 = this.compareCols(col, a) >= 0;
        const comp2 = this.compareCols(col, b) <= 0;

        return comp1 && comp2;
    }

    compareCols(a: Column, b: Column) {
        if(a.length > b.length) {
            return 1;
        } else if(a.length < b.length) {
            return -1;
        } 

        if(a > b) {
            return 1;
        } else if (a < b) {
            return -1;
        } 

        return 0;
    }

    saveSpreadsheet(): void {
        this.treegrid?.Save();
    }
}